/**
 * THE STAGED SEQUENCE for one account and one type.
 *
 * Three drafts — the ask, the nudge, the close — written from this order's own
 * facts. The rep reads them, EDITS them, and sends from their own mail client.
 *
 * NOTHING SENDS FROM HERE, deliberately. An email from Philip should come out
 * of Philip's mailbox with his signature and eleven years of history with that
 * coach behind it. Routed through an automation it becomes a mailshot, which
 * is the same failure as asking before delivery: it tells the customer they
 * are a row in a system.
 *
 * ⚠️ THE BODY IS NOW EDITABLE, AND THAT RE-OPENS A HAZARD.
 * Measured on the live app 2026-09-11: every draft the backend composes arrives
 * fully merged — real first names, real sport, real place ID, no `{{token}}`
 * and no "Hi Coach!" fallback. The placeholder hazard was NOT reproducing.
 * A read-only preview could not reintroduce it; a textarea can, the first time
 * somebody pastes a snippet or starts a line they mean to finish.
 *
 * So `draftTokens.js` scans what is actually in the box and an unresolved token
 * BLOCKS every route out of this panel — mailto, copy, and recording the ask.
 * It does not warn. A warning between a rep and a customer at 4pm is a warning
 * nobody reads.
 *
 * `missing` is shown rather than hidden. A draft that could not name the
 * championship should look thin, so the rep fixes it instead of sending a
 * generic note believing it was personalised.
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api.js'
import {reviewCopyBoundary} from './reviewCopyBoundary.js'
import {
  unresolvedTokens, blockReason,
  PHOTO_TYPES, withPhotoLine, withoutPhotoLine, hasPhotoLine,
} from './draftTokens.js'
import s from './reviews.module.css'

export default function DraftPanel({ orderId, type, account, onClose, onRecordAsk }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [step, setStep] = useState(0)
  const [copied, setCopied] = useState(false)

  // The edited body, per step. Seeded from the backend draft the first time a
  // step is opened; a rep's edit is never thrown away by switching steps.
  const [bodies, setBodies] = useState({})
  const [photo, setPhoto] = useState(PHOTO_TYPES.has(type))

  useEffect(() => {
    let live = true
    setData(null); setErr(null); setStep(0); setBodies({}); setPhoto(PHOTO_TYPES.has(type))
    api.reviewDraft(orderId, type)
      .then((d) => { if (live) setData(d) })
      .catch((e) => { if (live) setErr(e?.body?.reason || e?.message || 'Could not build the draft.') })
    return () => { live = false }
  }, [orderId, type])

  const draft = data?.drafts?.[step]

  /** The backend's body with the photo default applied — the starting point. */
  const seeded = useMemo(() => {
    if (!draft) return ''
    return photo ? withPhotoLine(draft.body,draft.step) : withoutPhotoLine(draft.body)
  }, [draft, photo])

  const body = bodies[step] !== undefined ? bodies[step] : seeded
  const edited = bodies[step] !== undefined && bodies[step] !== seeded

  const fullText = draft ? `${draft.greeting}\n\n${body}` : ''
  const scanned = `${draft?.subject || ''}\n${fullText}`
  const tokens = useMemo(() => unresolvedTokens(scanned), [scanned])
  const boundary=reviewCopyBoundary(draft,body,data?.askable)
  const blocked = boundary.blocked || tokens.length > 0
  const why = draft?.missing?.join(' ') || data?.blockedReason || blockReason(scanned) || boundary.findings.map(f=>f.say).join(' ') || (!boundary.signatureMatches?'Restore the current representative signature.':'')

  /* The mailto is REBUILT from what is on screen. Reusing the backend's
   * pre-baked `draft.mailto` would send the unedited body — the rep would
   * watch themselves make a change and then mail the version without it. */
  const mailto = useMemo(() => {
    if (!draft || blocked) return ''
    const to = encodeURIComponent(data?.to || '')
    const sub = encodeURIComponent(draft.subject || '')
    const b = encodeURIComponent(fullText)
    return `mailto:${to}?subject=${sub}&body=${b}`
  }, [draft, data, fullText, blocked])

  const setBody = (v) => setBodies((m) => ({ ...m, [step]: v }))

  const togglePhoto = () => {
    const next = !photo
    setPhoto(next)
    // Apply to what the rep is actually looking at, edits included.
    setBodies((m) => {
      const cur = m[step] !== undefined ? m[step] : (draft?.body || '')
      return { ...m, [step]: next ? withPhotoLine(cur,draft?.step) : withoutPhotoLine(cur) }
    })
  }

  const copy = async () => {
    if (!draft || blocked) return
    try {
      await navigator.clipboard.writeText(fullText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className={s.drawer} role="dialog" aria-label={`Review email drafts for ${account}`}>
      <div className={s.drawerHead}>
        <div>
          <div className={s.account}>{account}</div>
          <div className={s.meta}>
            <span className={s.chip}>{data?.typeMeta?.label || type}</span>
            {data?.typeMeta?.why && <span>{data.typeMeta.why}</span>}
          </div>
        </div>
        <button type="button" className={`${s.btn} ${s.quiet}`} onClick={onClose}>Close</button>
      </div>

      {err && <div className={s.empty}>{err}</div>}
      {!err && !data && <div className={s.empty}>Writing the drafts…</div>}

      {data && (
        <>
          {!data.askable && (
            <div className={s.warn}>
              <strong>Not askable yet.</strong> {data.blockedReason}
            </div>
          )}

          {data.askWho && (
            <div className={s.guidance}>
              This goes to <span className={s.askWho}>{data.askWho}</span>.
              {data.to ? ` On file: ${data.to}` : ' No email address on the order — you will need to copy this one.'}
            </div>
          )}

          <div className={s.stepRow} role="tablist" aria-label="Sequence step">
            {data.drafts.map((d, i) => (
              <button
                key={d.step}
                type="button"
                role="tab"
                aria-selected={i === step}
                className={`${s.stepBtn} ${i === step ? s.stepActive : ''}`}
                onClick={() => setStep(i)}
              >
                <span className={s.stepName}>{d.timing.label}</span>
                <span className={s.stepWhen}>
                  {d.timing.afterDays === 0 ? 'now' : `+${d.timing.afterDays}d`}
                </span>
              </button>
            ))}
          </div>

          {draft && (
            <>
              <p className={s.stepHint}>{draft.timing.when}</p>

              {draft.missing?.length > 0 && (
                <div className={s.warn}>
                  Written without: {draft.missing.join(', ')}. The draft leaves those
                  sentences out rather than papering over them — worth filling in before
                  you send.
                </div>
              )}

              <div className={s.subject}>
                <span className={s.subjectLabel}>Subject</span>
                {draft.subject}
              </div>

              <label className={s.srOnly} htmlFor={`body-${step}`}>
                Email body — edit before sending
              </label>
              <textarea
                id={`body-${step}`}
                className={`${s.draftBody} ${blocked ? s.draftBlocked : ''}`}
                value={body}
                spellCheck
                onChange={(e) => setBody(e.target.value)}
              />

              <div className={s.bodyBar}>
                {/* The photo ask. Brian's line, his words, offered as a default
                    rather than forced — it is the differentiator, and it is
                    also the thing a rep might reasonably cut for a coach who
                    has already sent twenty photos. */}
                <label className={s.toggle}>
                  <input
                    type="checkbox"
                    checked={hasPhotoLine(body)}
                    onChange={togglePhoto}
                    disabled={draft?.step!=='ask'}
                  />
                  <span>Ask for a photo</span>
                </label>
                {edited && (
                  <button
                    type="button"
                    className={`${s.btn} ${s.quiet}`}
                    onClick={() => setBodies((m) => { const n = { ...m }; delete n[step]; return n })}
                  >
                    Reset to the written draft
                  </button>
                )}
                <span className={s.bodyNote}>
                  {edited ? 'Edited — yours, not the system’s.' : 'Edit freely. Nothing sends from here.'}
                </span>
              </div>

              {/* THE BLOCK. Named tokens, not "invalid input". */}
              {blocked && (
                <div className={s.blocker} role="alert">
                  <strong>This draft cannot be sent yet.</strong> {why}
                </div>
              )}

              <div className={s.actions}>
                {blocked ? (
                  <button type="button" className={s.btn} disabled aria-disabled="true">
                    Open in mail
                  </button>
                ) : (
                  <a
                    className={s.btn}
                    href={mailto}
                  >
                    Open in mail
                  </a>
                )}
                <button
                  type="button" className={`${s.btn} ${s.ghost}`}
                  onClick={copy} disabled={blocked}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
                {onRecordAsk && (
                  <button
                    type="button" className={`${s.btn} ${s.ghost}`}
                    onClick={onRecordAsk} disabled={blocked}
                  >
                    I sent it — record the ask
                  </button>
                )}
              </div>

              <p className={s.note}>{data.sendPosture}</p>
            </>
          )}
        </>
      )}
    </div>
  )
}
